import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(tz);

const formatterCache = {};
function getFormatter(locale) {
    const cKey = locale || "en";
    let fmt = formatterCache[cKey];
    if (!fmt) {
        fmt = new Intl.DateTimeFormat(cKey, { year: "numeric", month: "numeric", day: "numeric", timeZone: "utc" });
        formatterCache[cKey] = fmt;
    }
    return fmt;
}

// We further process the date as relative if the difference is lower than 75 hours
const recentLimit = 75 * 60;

const MILLISECONDS_A_MINUTE = 60 * 1e3;
const MILLISECONDS_A_DAY = (24 * 3600) * 1e3;
const dayDiffer = (start, end) => {
    const startS = start.startOf("d");
    const endS = end.startOf("d");
    const diff = endS - startS;
    return Math.round((diff) / MILLISECONDS_A_DAY);
};

const localInTimezone = (timeZone, returnDayJS) => {
    let now = dayjs();
    try {
        const inZone = now.tz(timeZone); // throws if is invalid
        // read the time as utc, to compare it
        //now = inZone.utc(true); // true: keepLocalTime
        now = dayjs.utc(inZone.format("YYYY-MM-DD HH:mm:ss:SSS")); // avoid a bug with DST by using format instead of .utc()
    } catch {
        now = dayjs.utc(now.format("YYYY-MM-DD HH:mm:ss:SSS")); // we didnt change the tz
    }

    if (returnDayJS) return now;
    return now.toDate();
};

const getDateFromYearWeek = (yearWeek) => {
    const [year, week] = yearWeek.split("-").map(Number);
    const firstDayOfYear = new Date(year, 0, 1);
    const dayOfWeek = firstDayOfYear.getDay();
    const firstMondayOfYear = new Date(firstDayOfYear);
    firstMondayOfYear.setDate(
        firstDayOfYear.getDate() + (dayOfWeek <= 4 ? 1 - dayOfWeek : 8 - dayOfWeek)
    );

    const resultDate = new Date(firstMondayOfYear);
    resultDate.setDate(resultDate.getDate() + (week - 1) * 7);

    return resultDate;
};

const getGMT = (timeZone) => {
    const now = dayjs();
    const mins = now.tz(timeZone).utcOffset();

    const decimalTimeString = mins / 60;
    let decimalTime = parseFloat(decimalTimeString);
    decimalTime = decimalTime * 60 * 60;
    const hours = Math.floor((decimalTime / (60 * 60)));
    decimalTime = decimalTime - (hours * 60 * 60);
    let minutes = Math.floor((decimalTime / 60));
    decimalTime = decimalTime - (minutes * 60);
    /* if (hours < 10) {
    hours = "0" + hours;
  } */
    if (minutes < 10) {
        minutes = "0" + minutes;
    }
    return (`(GMT ${hours}:${minutes})`);
    /* return (<span style={{ fontSize: '10px' }}>{`(GMT ${hours}:${minutes})`}</span>); */
};

const getFormatedMinutes = (minutesDiff, isPast, t) => {
    const hours = Math.floor(minutesDiff / 60);
    const minutes = hours ? minutesDiff - (hours * 60) : minutesDiff;
    const hoursText = hours ? `${hours} ${t("date.hour") + (hours !== 1 ? "s" : "")}` : "";
    const minutesText = (!hours || minutes) ? `${minutes} ${t("date.minute") + (minutes !== 1 ? "s" : "")}` : "";

    const format = isPast ? t("date.pastTime") : t("date.futureTime");
    return format.replace("%s", (hours && minutes) ? `${hoursText} ${minutesText}` : (hours ? hoursText : minutesText));
};

// date is the ISO string
const utcToDate = (dateStr, timeZone, returnDayJS) => {
    let date = dayjs.utc(dateStr);
    try {
        const inZone = date.tz(timeZone); // throws if is invalid
        // read the time as utc, to compare it
        //date = inZone.utc(true); // true: keepLocalTime
        date = dayjs.utc(inZone.format("YYYY-MM-DD HH:mm:ss:SSS")); // avoid a bug with DST by using format instead of .utc()
    } catch {
    // we didnt change the tz
    }

    if (returnDayJS) return date;
    return date.toDate();
};

// format date that is in timezone
const dateFormat = (date, t, loc, timeZone, onlyDate, noRelative) => {
    if(!date) return "-";
    // we get the timedate without timezone, so parse it as utc
    const nDate = dayjs.utc(date);
    /*const now = localInTimezone(timeZone, true);

  let minutesDelta = ((nDate - now) / MILLISECONDS_A_MINUTE) >> 0;
  const isPast = minutesDelta <= 0;
  minutesDelta = Math.abs(minutesDelta); */

    let minutesDelta, now, isPast;
    if (!noRelative) {
        now = localInTimezone(timeZone, true);
        minutesDelta = ((nDate - now) / MILLISECONDS_A_MINUTE) >> 0;
        isPast = minutesDelta <= 0;
        minutesDelta = Math.abs(minutesDelta);
    } else minutesDelta = Math.Infinity;

    let preText = null;
    let inParentheses = false;
    // we show relative hours/minutes up to 2 hours.
    if (minutesDelta <= 120) {
        preText = getFormatedMinutes(minutesDelta, isPast, t);
        inParentheses = true;
    } else if (minutesDelta <= recentLimit) {
        const daysDiff = dayDiffer(now, nDate);
        if (daysDiff === 0) {
            preText = t("date.todayAt");
        } else if (daysDiff === 1) {
            preText = t("date.tomorrowAt");
        } else if (daysDiff === -1) {
            preText = t("date.yesterdayAt");
        }
    }

    if (!preText) {
        const jsDate = nDate.toDate();
        const fmt = getFormatter(loc);
        preText = `${fmt.format(jsDate)}`;
    }
    if (onlyDate) return preText;

    const paddedHours = `0${nDate.hour()}`.slice(-2);
    const paddedMinutes = `0${nDate.minute()}`.slice(-2);
    return inParentheses ? `${preText} (${paddedHours}:${paddedMinutes})` : `${preText} ${paddedHours}:${paddedMinutes}`;
};

const dateDiffInMinutes = (lastDate, timeZone) => {
    const lastDateToDate = new Date(lastDate);
    const localTime = localInTimezone(timeZone, false);
    return ((localTime - lastDateToDate) / MILLISECONDS_A_MINUTE) >> 0;
};

const dateDiffInDays = (dateFrom, dateTo) => {
    /* const date1utc = Date.UTC(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate());
  const date2utc = Date.UTC(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate());
  const day = 1000 * 60 * 60 * 24;
  return (date2utc - date1utc) / day; */

    if (dateTo < dateFrom) {
        const dTmp = dateTo;
        dateTo = dateFrom;
        dateTo = dTmp;
    }

    let months = (dateTo.getFullYear() - dateFrom.getFullYear()) * 12;
    months -= dateFrom.getMonth() + 1;
    months += dateTo.getMonth();

    if (dateFrom.getDate() <= dateTo.getDate()) months += 1;

    return months;
};



export { dateFormat, localInTimezone, dateDiffInMinutes, getGMT, dateDiffInDays, utcToDate, getDateFromYearWeek };
